import type { Signal } from '@angular/core'
import { inject, Injectable, computed } from '@angular/core'
import { ApiClient } from '@awork/_shared/services/api-client/ApiClient'
import { apiEndpoint } from '@awork/environments/environment'
import type { Observable } from 'rxjs'
import { catchError, finalize, map, of, shareReplay, tap } from 'rxjs'
import type { ICustomFieldSelectionOption } from '@awork/features/project/models/custom-field-definition.model'
import { CustomFieldDefinition } from '@awork/features/project/models/custom-field-definition.model'
import { CustomFieldDefinitionStore } from '@awork/features/project/state/custom-field-definition.store'
import { CustomFieldDefinitionQuery } from '@awork/features/project/state/custom-field-definition.query'
import type { CustomFieldDefinitionLinkEntity } from '@awork/features/project/models/custom-field-definition-link.model'
import { CustomFieldDefinitionLink } from '@awork/features/project/models/custom-field-definition-link.model'
import { arrayAdd, arrayRemove, arrayUpdate } from '@awork/core/state/signal-store/helpers'
import { ProjectStore } from '@awork/features/project/state/project.store'
import { ProjectTemplateStore } from '@awork/features/project/state/project-template.store'
import { ProjectQuery } from '@awork/features/project/state/project.query'
import { ProjectTemplateQuery } from '@awork/features/project/state/project-template.query'
import type { CustomFieldValue } from '../../models/custom-field.model'
import { CustomField } from '../../models/custom-field.model'
import { TaskStore } from '@awork/features/task/state/task.store'
import type { Task } from '@awork/features/task/models/task.model'
import type { TaskTemplate } from '@awork/features/task/models/task-template.model'
import { HttpParams } from '@angular/common/http'
import { generateResourceVersion } from '@awork/_shared/functions/generate-resource-version'
import type { Project } from '../../models/project.model'
import type { ProjectTemplate } from '../../models/project-template.model'
import { WorkflowStore } from '@awork/features/workflow/state/workflow.store'
import { WorkflowQuery } from '@awork/features/workflow/state/workflow.query'

@Injectable({
  providedIn: 'root'
})
export class CustomFieldService {
  private apiClient = inject(ApiClient)
  private customFieldDefinitionStore = inject(CustomFieldDefinitionStore)
  private customFieldDefinitionQuery = inject(CustomFieldDefinitionQuery)
  private projectStore = inject(ProjectStore)
  private projectQuery = inject(ProjectQuery)
  private projectTemplateStore = inject(ProjectTemplateStore)
  private projectTemplateQuery = inject(ProjectTemplateQuery)
  private taskStore = inject(TaskStore)
  private workflowStore = inject(WorkflowStore)
  private workflowQuery = inject(WorkflowQuery)

  private readonly customFieldDefinitionsUrl = `${apiEndpoint}/customfielddefinitions`
  private fetchCustomFieldDefinitionsIfNeeded$: Observable<CustomFieldDefinition[]> | undefined

  fetchedCustomFieldDefinitions = new Map<string, boolean>()

  /** CUSTOM FIELD DEFINITIONS **/

  /**
   * Fetches all custom field definitions
   * @param {boolean} includeLinkedProjectIds
   * @returns {Observable<CustomFieldDefinition[]>}
   */
  fetchCustomFieldDefinitions(includeLinkedProjectIds?: boolean): Observable<CustomFieldDefinition[]> {
    return this.apiClient
      .get<CustomFieldDefinition[]>(this.customFieldDefinitionsUrl, {
        params: includeLinkedProjectIds
          ? new HttpParams().set('includeLinkedProjectIds', includeLinkedProjectIds)
          : undefined
      })
      .pipe(
        map(response => {
          return response.map(customFieldDefinition => new CustomFieldDefinition(customFieldDefinition))
        }),
        tap(customFieldDefinitions => {
          this.customFieldDefinitionStore.set(customFieldDefinitions, 'fetchCustomFieldDefinitions')
          this.customFieldDefinitionStore.markAllDefinitionsLoaded()
        })
      )
  }

  /**
   * Fetches all custom field definitions unless the complete list already loaded.
   * @returns {Observable<CustomFieldDefinition[]>}
   */
  fetchCustomFieldDefinitionsIfNeeded(): Observable<CustomFieldDefinition[]> {
    const hasLoadedAllDefinitions = this.customFieldDefinitionStore.getHasLoadedAllDefinitions()

    if (hasLoadedAllDefinitions()) {
      return of(this.customFieldDefinitionQuery.getAll())
    }

    if (this.fetchCustomFieldDefinitionsIfNeeded$) {
      return this.fetchCustomFieldDefinitionsIfNeeded$
    }

    this.fetchCustomFieldDefinitionsIfNeeded$ = this.fetchCustomFieldDefinitions().pipe(
      finalize(() => {
        this.fetchCustomFieldDefinitionsIfNeeded$ = undefined
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    )

    return this.fetchCustomFieldDefinitionsIfNeeded$
  }

  /**
   * Creates a new custom field definition
   * @param {CustomFieldDefinition} customFieldDefinition
   * @returns {Observable<CustomFieldDefinition>}
   */
  sendCustomFieldDefinition(customFieldDefinition: CustomFieldDefinition): Observable<CustomFieldDefinition> {
    return this.apiClient.post<CustomFieldDefinition>(this.customFieldDefinitionsUrl, customFieldDefinition).pipe(
      map(response => {
        return new CustomFieldDefinition(response)
      }),
      tap(newCustomFieldDefinition => {
        this.customFieldDefinitionStore.add(newCustomFieldDefinition, 'sendCustomFieldDefinition')
      })
    )
  }

  /**
   * Updates a custom field definition
   * @param {CustomFieldDefinition} customFieldDefinition
   * @returns {Observable<CustomFieldDefinition>}
   */
  updateCustomFieldDefinition(customFieldDefinition: CustomFieldDefinition): Observable<CustomFieldDefinition> {
    return this.apiClient
      .put<CustomFieldDefinition>(
        `${this.customFieldDefinitionsUrl}/${customFieldDefinition.id}`,
        customFieldDefinition
      )
      .pipe(
        map(response => {
          return new CustomFieldDefinition(response)
        }),
        tap(customFieldDefinition => {
          this.customFieldDefinitionStore.update(
            customFieldDefinition.id,
            customFieldDefinition,
            'updateCustomFieldDefinition'
          )
        })
      )
  }

  /**
   * Deletes a custom field definition
   * @param {string} customFieldDefinitionId
   * @returns {Observable<void>}
   */
  deleteCustomFieldDefinition(customFieldDefinitionId: string): Observable<void> {
    return this.apiClient.delete<void>(`${this.customFieldDefinitionsUrl}/${customFieldDefinitionId}`).pipe(
      tap(() => {
        this.customFieldDefinitionStore.remove(customFieldDefinitionId, 'deleteCustomFieldDefinition')
      })
    )
  }

  /** CUSTOM FIELD DEFINITIONS SELECT OPTIONS **/

  /**
   * Creates a selection options of a custom field definition
   * @param {string} customFieldDefinitionId
   * @param {ICustomFieldSelectionOption} selectionOption
   * @returns {Observable<ICustomFieldSelectionOption>}
   */
  sendCustomFieldDefinitionSelectionOption(
    customFieldDefinitionId: string,
    selectionOption: ICustomFieldSelectionOption
  ): Observable<ICustomFieldSelectionOption> {
    return this.apiClient
      .post<ICustomFieldSelectionOption>(
        `${this.customFieldDefinitionsUrl}/${customFieldDefinitionId}/selectionOptions`,
        selectionOption
      )
      .pipe(
        tap(response => {
          this.customFieldDefinitionStore.update(
            customFieldDefinitionId,
            customFieldDefinition => ({
              selectionOptions: arrayAdd(customFieldDefinition.selectionOptions, response)
            }),
            'sendCustomFieldDefinitionSelectionOption'
          )
        })
      )
  }

  /**
   * Updates a selection option of a custom field definition
   * @param {string} customFieldDefinitionId
   * @param {ICustomFieldSelectionOption} selectionOption
   * @returns {Observable<ICustomFieldSelectionOption>}
   */
  updateCustomFieldDefinitionSelectionOption(
    customFieldDefinitionId: string,
    selectionOption: ICustomFieldSelectionOption
  ): Observable<ICustomFieldSelectionOption> {
    return this.apiClient
      .put<ICustomFieldSelectionOption>(
        `${this.customFieldDefinitionsUrl}/${customFieldDefinitionId}/selectionoptions/${selectionOption.id}`,
        selectionOption
      )
      .pipe(
        tap(response => {
          this.customFieldDefinitionStore.update(
            customFieldDefinitionId,
            customFieldDefinition => ({
              selectionOptions: arrayUpdate(customFieldDefinition.selectionOptions, selectionOption.id, response)
            }),
            'updateCustomFieldDefinitionSelectionOption'
          )
        })
      )
  }

  /**
   * Deletes a selection option of a custom field definition
   * @param {string} customFieldDefinitionId
   * @param {string} selectionOptionId
   * @returns {Observable<void>}
   */
  deleteCustomFieldDefinitionSelectionOption(
    customFieldDefinitionId: string,
    selectionOptionId: string
  ): Observable<void> {
    return this.apiClient
      .delete<void>(
        `${this.customFieldDefinitionsUrl}/${customFieldDefinitionId}/selectionoptions/${selectionOptionId}`
      )
      .pipe(
        tap(() => {
          this.customFieldDefinitionStore.update(
            customFieldDefinitionId,
            customFieldDefinition => ({
              selectionOptions: arrayRemove(customFieldDefinition.selectionOptions, selectionOptionId)
            }),
            'deleteCustomFieldDefinitionSelectionOption'
          )
        })
      )
  }

  /** CUSTOM FIELD DEFINITION LINKS - PROJECT / PROJECT TEMPLATE **/

  /**
   * Fetches all custom field definitions links of a project or project template
   * @param {string} entityId
   * @param {CustomFieldDefinitionLinkEntity} entityType
   * @returns {Observable<CustomFieldDefinitionLink[]>}
   */
  fetchProjectCustomFieldDefinitionLinks(
    entityId: string,
    entityType: CustomFieldDefinitionLinkEntity
  ): Observable<CustomFieldDefinitionLink[]> {
    return this.apiClient
      .get<CustomFieldDefinitionLink[]>(`${apiEndpoint}/${entityType.toLowerCase()}/${entityId}/customfielddefinitions`)
      .pipe(
        map(response => {
          return (response ?? []).map(link => {
            entityType === 'projects' ? (link.projectId = entityId) : (link.projectTemplateId = entityId)
            return new CustomFieldDefinitionLink(link)
          })
        }),
        tap(customFieldDefinitionLinks => {
          this.fetchedCustomFieldDefinitions.set(entityId, true)

          if (entityType === 'projects') {
            this.projectStore.update(entityId, { customFieldDefinitionLinks }, 'fetchProjectCustomFieldDefinitionLinks')
            this.syncProjectLinkedCustomFieldDefinitions(entityId, customFieldDefinitionLinks)
          } else {
            this.projectTemplateStore.update(
              entityId,
              { customFieldDefinitionLinks },
              'fetchProjectCustomFieldDefinitionLinks'
            )
          }
        })
      )
  }

  /**
   * Creates a new custom field definition link for a project or project template
   * @param {string} entityId
   * @param {CustomFieldDefinitionLinkEntity} entityType
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @returns {Observable<CustomFieldDefinitionLink>}
   */
  sendProjectCustomFieldDefinitionLink(
    entityId: string,
    entityType: CustomFieldDefinitionLinkEntity,
    customFieldDefinitionLink: CustomFieldDefinitionLink
  ): Observable<CustomFieldDefinitionLink> {
    return this.apiClient
      .post<void>(`${apiEndpoint}/${entityType.toLowerCase()}/${entityId}/linkcustomfielddefinition `, {
        customFieldDefinitionId: customFieldDefinitionLink.id,
        order: customFieldDefinitionLink.order
      })
      .pipe(
        map(() => {
          return customFieldDefinitionLink
        }),
        tap(newCustomFieldDefinitionLink => {
          if (entityType === 'projects') {
            this.projectStore.update(
              entityId,
              project => ({
                customFieldDefinitionLinks: arrayAdd(project.customFieldDefinitionLinks, newCustomFieldDefinitionLink)
              }),
              'sendProjectCustomFieldDefinitionLink'
            )
            this.addProjectIdToCustomFieldDefinition(newCustomFieldDefinitionLink.id, entityId)
          } else {
            this.projectTemplateStore.update(
              entityId,
              projectTemplate => ({
                customFieldDefinitionLinks: arrayAdd(
                  projectTemplate.customFieldDefinitionLinks,
                  newCustomFieldDefinitionLink
                )
              }),
              'sendProjectCustomFieldDefinitionLink'
            )
          }
        })
      )
  }

  /**
   * Updates a custom field definition link of a project or project template
   * @param {string} entityId
   * @param {CustomFieldDefinitionLinkEntity} entityType
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @returns {Observable<CustomFieldDefinitionLink>}
   */
  updateProjectCustomFieldDefinitionLink(
    entityId: string,
    entityType: CustomFieldDefinitionLinkEntity,
    customFieldDefinitionLink: CustomFieldDefinitionLink
  ): Observable<CustomFieldDefinitionLink> {
    let operationId: string

    if (entityType === 'projects') {
      const project = this.projectQuery.queryProject(entityId)

      operationId = this.projectStore.history.startOperation({ type: 'update', entities: [project()] })

      this.projectStore.update(
        entityId,
        project => ({
          customFieldDefinitionLinks: arrayUpdate(
            project.customFieldDefinitionLinks,
            customFieldDefinitionLink.id,
            customFieldDefinitionLink
          )
        }),
        'updateProjectCustomFieldDefinitionLink'
      )
    } else {
      const projectTemplate = this.projectTemplateQuery.queryProjectTemplate(entityId)

      operationId = this.projectTemplateStore.history.startOperation({ type: 'update', entities: [projectTemplate()] })

      this.projectTemplateStore.update(
        entityId,
        projectTemplate => ({
          customFieldDefinitionLinks: arrayUpdate(
            projectTemplate.customFieldDefinitionLinks,
            customFieldDefinitionLink.id,
            customFieldDefinitionLink
          )
        }),
        'updateProjectCustomFieldDefinitionLink'
      )
    }

    return this.apiClient
      .put<{
        order: number
      }>(
        `${apiEndpoint}/${entityType.toLowerCase()}/${entityId.toLowerCase()}/customfielddefinitions/${customFieldDefinitionLink.id}`,
        { order: customFieldDefinitionLink.order }
      )
      .pipe(
        map(response => {
          return new CustomFieldDefinitionLink({ ...customFieldDefinitionLink, order: response.order })
        }),
        tap(updatedCustomFieldDefinitionLink => {
          if (entityType === 'projects') {
            this.projectStore.history.endOperation(operationId)
            this.projectStore.update(
              entityId,
              project => ({
                customFieldDefinitionLinks: arrayUpdate(
                  project.customFieldDefinitionLinks,
                  customFieldDefinitionLink.id,
                  updatedCustomFieldDefinitionLink
                )
              }),
              'updateProjectCustomFieldDefinitionLink'
            )
          } else {
            this.projectTemplateStore.history.endOperation(operationId)
            this.projectTemplateStore.update(
              entityId,
              projectTemplate => ({
                customFieldDefinitionLinks: arrayUpdate(
                  projectTemplate.customFieldDefinitionLinks,
                  customFieldDefinitionLink.id,
                  updatedCustomFieldDefinitionLink
                )
              }),
              'updateProjectCustomFieldDefinitionLink'
            )
          }
        }),
        catchError(error => {
          entityType === 'projects'
            ? this.projectStore.history.undo(operationId)
            : this.projectTemplateStore.history.undo(operationId)
          throw error
        })
      )
  }

  /**
   * Deletes a custom field definition link of a project or project template
   * @param {string} entityId
   * @param {CustomFieldDefinitionLinkEntity} entityType
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @returns {Observable<void>}
   */
  deleteProjectCustomFieldDefinitionLink(
    entityId: string,
    entityType: CustomFieldDefinitionLinkEntity,
    customFieldDefinitionLink: CustomFieldDefinitionLink
  ): Observable<void> {
    return this.apiClient
      .post<void>(`${apiEndpoint}/${entityType.toLowerCase()}/${entityId}/unlinkcustomfielddefinition`, {
        customFieldDefinitionId: customFieldDefinitionLink.id
      })
      .pipe(
        tap(() => {
          if (entityType === 'projects') {
            this.projectStore.update(
              entityId,
              project => ({
                customFieldDefinitionLinks: arrayRemove(
                  project.customFieldDefinitionLinks,
                  customFieldDefinitionLink.id
                )
              }),
              'deleteProjectCustomFieldDefinitionLink'
            )
            this.removeProjectIdFromCustomFieldDefinition(customFieldDefinitionLink.id, entityId)
          } else {
            this.projectTemplateStore.update(
              entityId,
              projectTemplate => ({
                customFieldDefinitionLinks: arrayRemove(
                  projectTemplate.customFieldDefinitionLinks,
                  customFieldDefinitionLink.id
                )
              }),
              'deleteProjectCustomFieldDefinitionLink'
            )
          }
        })
      )
  }

  /**
   * Syncs linked project ids on loaded workspace definitions after fetching project links.
   * @param {string} projectId
   * @param {CustomFieldDefinitionLink[]} customFieldDefinitionLinks
   */
  private syncProjectLinkedCustomFieldDefinitions(
    projectId: string,
    customFieldDefinitionLinks: CustomFieldDefinitionLink[]
  ): void {
    const linkedCustomFieldDefinitionIds = new Set(customFieldDefinitionLinks.map(link => link.id))
    const customFieldDefinitions = this.customFieldDefinitionQuery.getAll() ?? []

    customFieldDefinitions.forEach(customFieldDefinition => {
      if (linkedCustomFieldDefinitionIds.has(customFieldDefinition.id)) {
        this.addProjectIdToCustomFieldDefinition(customFieldDefinition.id, projectId)

        return
      }

      this.removeProjectIdFromCustomFieldDefinition(customFieldDefinition.id, projectId)
    })
  }

  /**
   * Adds a project id to a loaded custom-field definition's linked project ids.
   * @param {string} customFieldDefinitionId
   * @param {string} projectId
   */
  private addProjectIdToCustomFieldDefinition(customFieldDefinitionId: string, projectId: string): void {
    this.customFieldDefinitionStore.update(
      customFieldDefinitionId,
      customFieldDefinition => ({
        linkedProjectIds: Array.from(new Set([...(customFieldDefinition.linkedProjectIds ?? []), projectId]))
      }),
      'addProjectIdToCustomFieldDefinition'
    )
  }

  /**
   * Removes a project id from a loaded custom-field definition's linked project ids.
   * @param {string} customFieldDefinitionId
   * @param {string} projectId
   */
  private removeProjectIdFromCustomFieldDefinition(customFieldDefinitionId: string, projectId: string): void {
    this.customFieldDefinitionStore.update(
      customFieldDefinitionId,
      customFieldDefinition => ({
        linkedProjectIds: arrayRemove(customFieldDefinition.linkedProjectIds ?? [], projectId)
      }),
      'removeProjectIdFromCustomFieldDefinition'
    )
  }

  /** TASK CUSTOM FIELDS **/

  /**
   * Updates the custom fields of a task in the store optimistically.
   * The resource version of the task is updated to prevent store updates from pending requests.
   * @param {Task} task
   * @param {CustomField[]} customFields
   */
  updateTaskCustomFieldsInStore(task: Task, customFields: CustomField[]): void {
    if (!customFields?.length) {
      return
    }

    this.taskStore.update(
      task.id,
      task => {
        const updatedCustomFieldDefinitionIds = customFields.map(customField => customField.customFieldDefinitionId)
        const existingCustomFields = (task.customFields || []).filter(
          storedCustomField => !updatedCustomFieldDefinitionIds.includes(storedCustomField.customFieldDefinitionId)
        )

        const normalizedCustomFields = this.normalizeSelectionOptions(customFields)

        return {
          customFields: [...existingCustomFields, ...normalizedCustomFields],
          resourceVersion: generateResourceVersion()
        }
      },
      'updateTaskCustomFieldsInStore'
    )
  }

  /**
   * Updates the custom fields of a project in the store optimistically.
   * The resource version of the task is updated to prevent store updates from pending requests.
   * @param {Project} project
   * @param {CustomField[]} customFields
   */
  updateProjectCustomFieldsInStore(project: Project, customFields: CustomField[]): void {
    if (!customFields?.length) {
      return
    }

    this.projectStore.update(
      project.id,
      project => {
        const updatedCustomFieldDefinitionIds = customFields.map(customField => customField.customFieldDefinitionId)
        const existingCustomFields = (project.customFields || []).filter(
          storedCustomField => !updatedCustomFieldDefinitionIds.includes(storedCustomField.customFieldDefinitionId)
        )

        const normalizedCustomFields = this.normalizeSelectionOptions(customFields)

        return {
          customFields: [...existingCustomFields, ...normalizedCustomFields],
          resourceVersion: generateResourceVersion()
        }
      },
      'updateProjectCustomFieldsInStore'
    )
  }

  /**
   * Converts custom fields to post models for API requests
   * @param {CustomField[]} customFields
   * @returns {Partial<CustomField>[]}
   * @private
   */
  private getPostModels(customFields: CustomField[]): Partial<CustomField>[] {
    return customFields.map(customField => {
      const postModel: Partial<CustomField> = {
        customFieldDefinitionId: customField.customFieldDefinitionId,
        textValue: customField.textValue,
        numberValue: customField.numberValue,
        dateValue: customField.dateValue,
        booleanValue: customField.booleanValue,
        userIdValue: customField.userIdValue,
        clientIdValue: customField.clientIdValue,
        selectionOptionIdValue: customField.selectionOptionIdValue
      }

      // Remove undefined values to keep payload clean
      Object.keys(postModel).forEach(key => {
        if (postModel[key] === undefined) {
          delete postModel[key]
        }
      })

      return postModel
    })
  }

  /**
   * Normalizes custom fields by syncing the embedded selectionOption with selectionOptionIdValue.
   * When selectionOptionIdValue is set, populates selectionOption from the definition's options.
   * When selectionOptionIdValue is unset, clears the embedded selectionOption.
   * @param {CustomField[]} customFields
   * @returns {CustomField[]}
   * @private
   */
  private normalizeSelectionOptions(customFields: CustomField[]): CustomField[] {
    return customFields.map(cf => {
      const normalized = { ...cf } as CustomField & { selectionOption?: ICustomFieldSelectionOption }

      if (normalized.selectionOptionIdValue) {
        normalized.selectionOption =
          cf.customFieldDefinition?.selectionOptions?.find(opt => opt.id === normalized.selectionOptionIdValue) ?? null
      } else {
        normalized.selectionOption = null
      }

      return normalized as CustomField
    })
  }

  /**
   * Sets the custom fields of a task
   * @param {Task} task
   * @param {CustomField[]} customFields
   * @returns {Observable<void>}
   */
  setTaskCustomFields(task: Task, customFields: CustomField[]): Observable<void> {
    if (!customFields || !task.projectId) {
      return of(null)
    }

    const postModels = this.getPostModels(customFields)

    const operationId = this.taskStore.history.startOperation({ type: 'update', entities: [task] })

    this.updateTaskCustomFieldsInStore(task, customFields)

    return this.apiClient
      .post<void>(`${apiEndpoint}/tasks/${task.id}/setcustomfields`, postModels, {
        entityName: 'task',
        propertyName: 'customFields'
      })
      .pipe(
        tap(() => {
          this.taskStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.taskStore.history.undo(operationId)
          throw error
        })
      )
  }

  /**
   * Sets the custom fields of a task template
   * @param {TaskTemplate} taskTemplate
   * @param {CustomField[]} customFields
   * @returns {Observable<void>}
   */
  setTaskTemplateCustomFields(taskTemplate: TaskTemplate, customFields: CustomField[]): Observable<void> {
    if (!customFields || !taskTemplate.projectTemplateId) {
      return of(null)
    }

    const postModels = this.getPostModels(customFields)

    return this.apiClient.post<void>(`${apiEndpoint}/tasktemplates/${taskTemplate.id}/setcustomfields`, postModels)
  }

  /**
   * Sets the custom fields of a project
   * @param {Project} project
   * @param {CustomField[]} customFields
   * @returns {Observable<void>}
   */
  setProjectCustomFields(project: Project, customFields: CustomField[]): Observable<void> {
    if (!customFields || !project.id) {
      return of(null)
    }

    const postModels = this.getPostModels(customFields)

    const operationId = this.projectStore.history.startOperation({ type: 'update', entities: [project] })

    this.updateProjectCustomFieldsInStore(project, customFields)

    return this.apiClient
      .post<void>(`${apiEndpoint}/projects/${project.id}/setcustomfields`, postModels, {
        entityName: 'project',
        propertyName: 'customFields'
      })
      .pipe(
        tap(() => {
          this.projectStore.history.endOperation(operationId)
        }),
        catchError(error => {
          this.projectStore.history.undo(operationId)
          throw error
        })
      )
  }

  /**
   * Sets the custom fields of a project template
   * @param {ProjectTemplate} projectTemplate
   * @param {CustomField[]} customFields
   * @returns {Observable<void>}
   */
  setProjectTemplateCustomFields(projectTemplate: ProjectTemplate, customFields: CustomField[]): Observable<void> {
    if (!customFields || !projectTemplate.projectTypeId) {
      return of(null)
    }

    const postModels = this.getPostModels(customFields)

    return this.apiClient.post<void>(
      `${apiEndpoint}/projecttemplates/${projectTemplate.id}/setcustomfields`,
      postModels
    )
  }

  /** PROJECT CUSTOM FIELDS **/

  /**
   * Updates the order of custom field definitions
   * @param {CustomFieldDefinition} customFieldDefinition
   * @returns {Observable<void>}
   */
  updateProjectCustomFieldDefinitions(customFieldDefinition: CustomFieldDefinition): Observable<void> {
    return this.apiClient
      .put<void>(`${apiEndpoint}/customfielddefinitions/${customFieldDefinition.id}`, {
        name: customFieldDefinition.name,
        order: customFieldDefinition.order,
        type: customFieldDefinition.type,
        selectionOptions: customFieldDefinition.selectionOptions
      })
      .pipe(
        tap(() => {
          this.customFieldDefinitionStore.update(
            customFieldDefinition.id,
            customFieldDefinition,
            'updateProjectCustomFieldDefinitions'
          )
        })
      )
  }

  /**
   * Fetches custom field values for a specific project
   * @param {string} projectId
   * @returns {Observable<CustomField[]>}
   */
  fetchProjectCustomFieldValues(projectId: string): Observable<CustomField[]> {
    return this.apiClient.get<CustomField[]>(`${apiEndpoint}/projects/${projectId}/customfields`).pipe(
      map(response => response.map(field => new CustomField(field))),
      tap(customFields => {
        this.projectStore.update(projectId, project => ({
          ...project,
          customFields
        }))
      })
    )
  }

  /**
   * Queries custom fields for a project by custom field definition IDs
   * Returns a signal that combines custom field definitions with project custom field values
   * @param {Project} project
   * @param {string[]} customFieldDefinitionIds
   * @returns {Signal<CustomField[]>}
   */
  queryProjectCustomFields(project: Project, customFieldDefinitionIds: string[]): Signal<CustomField[]> {
    return computed(() => this.getProjectCustomFields(project, customFieldDefinitionIds))
  }

  /**
   * Returns custom fields for a project by custom field definition ids.
   * @param {Project} project
   * @param {string[]} customFieldDefinitionIds
   * @returns {CustomField[]}
   * @withSignal {CustomFieldDefinition[]} customFieldDefinitionQuery.queryCustomFieldDefinitions
   */
  private getProjectCustomFields(project: Project, customFieldDefinitionIds: string[]): CustomField[] {
    if (!project || !customFieldDefinitionIds?.length) {
      return []
    }

    const projectCustomFieldDefinitions = this.customFieldDefinitionQuery.queryCustomFieldDefinitions('project')
    const customFieldDefinitions = projectCustomFieldDefinitions().filter(def =>
      customFieldDefinitionIds.includes(def.id)
    )

    return customFieldDefinitionIds
      .map(id => this.getProjectCustomField(project, customFieldDefinitions, id))
      .filter((customField): customField is CustomField => !!customField)
      .sort((a, b) => {
        const orderA = a.customFieldDefinition?.order || 0
        const orderB = b.customFieldDefinition?.order || 0

        return orderA - orderB
      })
      .map(field => {
        return new CustomField({
          ...field,
          customFieldDefinition: this.convertToCustomFieldDefinitionLink(field.customFieldDefinition)
        })
      })
  }

  /**
   * Returns a project custom field for a custom field definition id.
   * @param {Project} project
   * @param {CustomFieldDefinition[]} customFieldDefinitions
   * @param {string} customFieldDefinitionId
   * @returns {CustomField | null}
   */
  private getProjectCustomField(
    project: Project,
    customFieldDefinitions: CustomFieldDefinition[],
    customFieldDefinitionId: string
  ): CustomField | null {
    const existingCustomField = project.customFields?.find(
      customField => customField.customFieldDefinitionId === customFieldDefinitionId
    )
    const customFieldDefinition = customFieldDefinitions.find(definition => definition.id === customFieldDefinitionId)

    if (!customFieldDefinition) {
      return null
    }

    const customFieldDefinitionLink = this.convertToCustomFieldDefinitionLink(customFieldDefinition)

    if (existingCustomField) {
      existingCustomField.customFieldDefinition = customFieldDefinitionLink

      return existingCustomField
    }

    return new CustomField({
      customFieldDefinitionId,
      customFieldDefinition: customFieldDefinitionLink
    })
  }

  /**
   * Converts a CustomFieldDefinition to a CustomFieldDefinitionLink
   * @param {CustomFieldDefinition} definition
   * @returns {CustomFieldDefinitionLink}
   * @private
   */
  private convertToCustomFieldDefinitionLink(definition: CustomFieldDefinition): CustomFieldDefinitionLink {
    if (!definition) {
      return null
    }

    return new CustomFieldDefinitionLink({
      ...definition,
      order: definition.order || 0
    })
  }

  /**
   * Updates a custom field value for a project
   * @param {string} projectId
   * @param {string} customFieldId
   * @param {CustomFieldValue} value
   * @returns {Observable<CustomField>}
   */
  updateProjectCustomFieldValue(
    projectId: string,
    customFieldId: string,
    value: CustomFieldValue
  ): Observable<CustomField> {
    return this.apiClient
      .put<CustomField>(`${apiEndpoint}/projects/${projectId}/customfields/${customFieldId}`, { value })
      .pipe(
        map(response => new CustomField(response)),
        tap(updatedField => {
          const projectQuery = this.projectQuery.queryProject(projectId)
          const project = projectQuery()

          if (project) {
            const existingFields = project['customFields'] || []
            const fieldIndex = existingFields.findIndex(field => field.customFieldDefinitionId === customFieldId)

            if (fieldIndex !== -1) {
              const updatedFields = [...existingFields]

              updatedFields[fieldIndex] = updatedField

              this.projectStore.update(projectId, proj => ({
                ...proj,
                customFields: updatedFields
              }))
            } else {
              this.projectStore.update(projectId, proj => ({
                ...proj,
                customFields: [...existingFields, updatedField]
              }))
            }
          }
        })
      )
  }

  /**
   * Fetches projects filtered by custom field values
   * @param {unknown[]} filters
   * @returns {Observable<Project[]>}
   */
  fetchProjectsWithCustomFieldFilters(filters: unknown[]): Observable<Project[]> {
    return this.apiClient.post<Project[]>(`${apiEndpoint}/projects/filter/customfields`, { filters }).pipe(
      map(response => response),
      tap(projects => {
        this.projectStore.upsertMany(projects)
      })
    )
  }

  /** WORKFLOW CUSTOM FIELD DEFINITION LINKS **/

  /**
   * Fetches all custom field definition links for a workflow
   * @param {string} workflowId
   * @returns {Observable<CustomFieldDefinitionLink[]>}
   */
  fetchWorkflowCustomFieldDefinitionLinks(workflowId: string): Observable<CustomFieldDefinitionLink[]> {
    return this.apiClient
      .get<CustomFieldDefinitionLink[]>(`${apiEndpoint}/workflows/${workflowId}/customfielddefinitions`)
      .pipe(
        map(response => {
          return response.map(link => {
            link.workflowId = workflowId
            return new CustomFieldDefinitionLink(link)
          })
        }),
        tap(customFieldDefinitionLinks => {
          this.fetchedCustomFieldDefinitions.set(workflowId, true)
          this.workflowStore.update(
            workflowId,
            { customFieldDefinitionLinks },
            'fetchWorkflowCustomFieldDefinitionLinks'
          )
        })
      )
  }

  /**
   * Links a custom field definition to a workflow
   * @param {string} workflowId
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @returns {Observable<CustomFieldDefinitionLink>}
   */
  sendWorkflowCustomFieldDefinitionLink(
    workflowId: string,
    customFieldDefinitionLink: CustomFieldDefinitionLink
  ): Observable<CustomFieldDefinitionLink> {
    return this.apiClient
      .post<void>(`${apiEndpoint}/workflows/${workflowId}/linkcustomfielddefinition`, {
        customFieldDefinitionId: customFieldDefinitionLink.id,
        order: customFieldDefinitionLink.order
      })
      .pipe(
        map(() => customFieldDefinitionLink),
        tap(newCustomFieldDefinitionLink => {
          this.workflowStore.update(
            workflowId,
            workflow => ({
              customFieldDefinitionLinks: arrayAdd(workflow.customFieldDefinitionLinks, newCustomFieldDefinitionLink)
            }),
            'sendWorkflowCustomFieldDefinitionLink'
          )
        })
      )
  }

  /**
   * Updates a custom field definition link order for a workflow
   * @param {string} workflowId
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @returns {Observable<CustomFieldDefinitionLink>}
   */
  updateWorkflowCustomFieldDefinitionLink(
    workflowId: string,
    customFieldDefinitionLink: CustomFieldDefinitionLink
  ): Observable<CustomFieldDefinitionLink> {
    const workflowQuery = this.workflowQuery.queryWorkflow(workflowId)
    const workflow = workflowQuery()

    const operationId = workflow
      ? this.workflowStore.history.startOperation({ type: 'update', entities: [workflow] })
      : null

    this.workflowStore.update(
      workflowId,
      workflow => ({
        customFieldDefinitionLinks: arrayUpdate(
          workflow.customFieldDefinitionLinks,
          customFieldDefinitionLink.id,
          customFieldDefinitionLink
        )
      }),
      'updateWorkflowCustomFieldDefinitionLink'
    )

    return this.apiClient
      .put<{
        order: number
      }>(`${apiEndpoint}/workflows/${workflowId}/customfielddefinitions/${customFieldDefinitionLink.id}`, {
        order: customFieldDefinitionLink.order
      })
      .pipe(
        map(response => new CustomFieldDefinitionLink({ ...customFieldDefinitionLink, order: response.order })),
        tap(updatedCustomFieldDefinitionLink => {
          if (operationId) {
            this.workflowStore.history.endOperation(operationId)
          }

          this.workflowStore.update(
            workflowId,
            workflow => ({
              customFieldDefinitionLinks: arrayUpdate(
                workflow.customFieldDefinitionLinks,
                customFieldDefinitionLink.id,
                updatedCustomFieldDefinitionLink
              )
            }),
            'updateWorkflowCustomFieldDefinitionLink'
          )
        }),
        catchError(error => {
          if (operationId) {
            this.workflowStore.history.undo(operationId)
          }

          throw error
        })
      )
  }

  /**
   * Unlinks a custom field definition from a workflow
   * @param {string} workflowId
   * @param {CustomFieldDefinitionLink} customFieldDefinitionLink
   * @param {boolean} [unlinkCustomFieldFromLinkedProjects=false]
   * @returns {Observable<void>}
   */
  deleteWorkflowCustomFieldDefinitionLink(
    workflowId: string,
    customFieldDefinitionLink: CustomFieldDefinitionLink,
    unlinkCustomFieldFromLinkedProjects = false
  ): Observable<void> {
    return this.apiClient
      .post<void>(`${apiEndpoint}/workflows/${workflowId}/unlinkcustomfielddefinition`, {
        customFieldDefinitionId: customFieldDefinitionLink.id,
        unlinkCustomFieldFromLinkedProjects
      })
      .pipe(
        tap(() => {
          this.workflowStore.update(
            workflowId,
            workflow => ({
              customFieldDefinitionLinks: arrayRemove(workflow.customFieldDefinitionLinks, customFieldDefinitionLink.id)
            }),
            'deleteWorkflowCustomFieldDefinitionLink'
          )
        })
      )
  }
}
